<script>
	import Helper from '../../Classes/Helper';
	import Preference from '../../Stores/Preference';
	import Product from '../../Stores/Product';
	import Widget from '../../Stores/Widget';
	import {onMount} from 'svelte';

	export let config;

	let input = {};
	let select = {};
	let element = {};

	async function getPreference() {
		const step = $Product.step.name;
		const group = $Product.group.name;
		const property = config.label;
		const value = await Preference.getContextValue(step, group, property);
		return value;
	}

	async function setPreference(value) {
		const step = $Product.step.name;
		const group = $Product.group.name;
		const property = config.label;
		await Preference.setContextValue(step, group, property, value);
	}

	function getSelected(element) {
		const menu = config.type === 'select';
		const option = menu ? select.options[select.selectedIndex] : {};
		const text = menu ? option.text : element.value;

		let value = element.value;
		let valid = !!menu && !!value;
		switch (config.type) {
			case 'more':
				value = Number(value);
				const validate = config.minimum || config.maximum;
				const range = (value >= config.minimum) && (value <= config.maximum);
				valid = (!value && !config.required) || !validate || (!isNaN(value) && range);
				break;
			case 'select': case 'number':
				value = Number(value);
				valid = !isNaN(value) && value;
				break;
			case 'text':
				valid = config.required ? !!value : true;
				break;
		}

		const state = value ? (valid ? 'success' : 'error') : 'empty';
		const label = config.unit ? `${text} ${config.unit}` : text;
		const selected = {value, label, text, valid, state};
		return selected;
	}

	function onInput(event) {
		if (config.on_input) {
			const selected = getSelected(input);
			config.on_input(selected);
		}
	}

	async function onInputChange(event) {
		if (config.on_change) {
			const selected = getSelected(input);
			config.on_change(selected);
			await setPreference(selected.text);
		}
	}

	async function onSelectChange(event) {
		if (config.on_change) {
			const selected = getSelected(select);
			config.on_change(selected);
			await setPreference(selected.text);
		}
	}

	function description(content) {
		const pattern = /\ssrc="\/storage\//ig;
		const replacement = ' src="https://shop.zonenscherm.nl/e2/zonscherm/site/custom/';
		return content.replace(pattern, replacement);
	}

	async function preference() {
		const success = config.state == 'success';
		if (success && !Helper.is(config.value)) {
			const value = await getPreference();
			if (value) {
				if (Helper.is(select)) {
					for (let loop = 0; loop < select.options.length; loop++)
						if (select.options[loop].text === value) {
							select.selectedIndex = loop;
							const event = new window.Event('change');
							select.dispatchEvent(event);
							break;
						}
				}

				else if (Helper.is(input)) {
					input.value = value;
					const event = new window.Event('change');
					input.dispatchEvent(event);
				}
			}
		}
	}

	async function mount() {
		await preference();
	}

	$: if (config.active) $Widget.current = element;
	onMount(mount);
</script>

<style>
	div.zes-input {
		margin-bottom: 1.5em;
	}

	div.zes-input-label {
		font-weight: 600;
		margin-bottom: .75em;
		color: var(--zes-1-2);
	}

	div.zes-input-field {
		font-size: 1.25em;
		position: relative;
	}

	div.zes-input-field input,
	div.zes-input-field select {
		font: inherit;
		font-weight: 600;
		background: hsl(0deg 0% 95%);
		padding: .5em 1em;
		border: 1px solid hsl(0deg 0% 95%);
		border-radius: .35em;
		display: block;
		width: 100%;
	}

	div.zes-input-field select {
		-moz-appearance: none;
		-webkit-appearance: none;
	}

	input.zes-active,
	select.zes-active,
	div.zes-input-field input:focus,
	div.zes-input-field select:focus {
		color: var(--zes-1-1);
		border-color: var(--zes-1-3);
		box-shadow: 0 0 .5em 0 var(--zes-1-3);
		background: var(--zes-1-4);
	}

	div.zes-input-field div {
		padding: .5em 1em;
		position: absolute;
		right: 0;
	}

	div.zes-input-field div span {
		font-size: .85em;
		font-weight: 700;
		color: var(--zes-1-3);
	}

	div.zes-input-warning {
		font-size: .75em;
		font-weight: 500;
		margin-top: .75em;
		display: block;
		color: var(--zes-1-3);
	}

	div.zes-input-warning i {
		margin-right: .5em;
	}

	div.zes-input-description {
		margin-top: 1em;
	}

	div.zes-input-description p {
		font-size: .8em;
		color: var(--zes-3-1);
	}

	div.zes-input-description p i {
		margin-right: .5em;
	}

	/* Success */

	div.zes-input.zes-success div.zes-input-label {
		color: var(--zes-2-1);
	}

	div.zes-input.zes-success div.zes-input-field input,
	div.zes-input.zes-success div.zes-input-field select {
		color: var(--zes-2-1);
		background: hsl(var(--zes-2-1) / .1);
	}

	div.zes-input.zes-success div.zes-input-field input.zes-active,
	div.zes-input.zes-success div.zes-input-field select.zes-active,
	div.zes-input.zes-success div.zes-input-field input:focus {
		border-color: var(--zes-2-1);
		box-shadow: 0 0 .5em 0 var(--zes-2-1);
	}

	div.zes-input.zes-success div.zes-input-field div span {
		color: var(--zes-2-2);
	}

	div.zes-input.zes-success div.zes-input-warning {
		color: var(--zes-2-2);
	}

	/* Error */

	div.zes-input.zes-error div.zes-input-label {
		color: var(--zes-4-1);
	}

	div.zes-input.zes-error div.zes-input-field input {
		color: var(--zes-4-1);
		background: hsl(var(--zes-4-1) / .1);
	}

	div.zes-input.zes-error div.zes-input-field input.zes-active,
	div.zes-input.zes-error div.zes-input-field select.zes-active,
	div.zes-input.zes-error div.zes-input-field input:focus {
		border-color: var(--zes-4-1);
		box-shadow: 0 0 .5em 0 var(--zes-4-1);
	}

	div.zes-input.zes-error div.zes-input-field div span {
		color: var(--zes-4-2);
	}

	div.zes-input.zes-error div.zes-input-warning {
		color: var(--zes-4-2);
	}

	/* Description */

	div.zes-input-description-content {
		margin: 1.5em 0;
	}

	:global(div.zes-input-description > div.zes-input-description-content p) {
		line-height: 1.5em;
		margin: 1em 0;
	}

	:global(div.zes-input-description > div.zes-input-description-content p:first-child),
	:global(div.zes-input-description > div.zes-input-description-content p:first-child strong) {
		border-top: none;
		padding-top: 0;
		margin-top: 0;
	}

	:global(div.zes-input-description > div.zes-input-description-content p:empty),
	:global(div.zes-input-description > div.zes-input-description-content strong:empty) {
		display: none;
	}

	:global(div.zes-input-description > div.zes-input-description-content strong) {
		display: block;
		font-weight: 600;
		color: var(--zes-1-2);
		padding-top: 1.5em;
		margin-top: 1em;
		border-top: 2px solid var(--zes-1-4);
	}

	:global(div.zes-input-description > div.zes-input-description-content a) {
		font-weight: 600;
		color: var(--zes-1-2);
	}
</style>

<div bind:this="{element}" class="zes-input zes-{config.state}">
	<div class="zes-input-label">{config.label}</div>
	<div class="zes-input-field">
		{#if config.unit}
			<div>
				<span>{config.unit}</span>
			</div>
		{/if}
		{#if ['text'].includes(config.type)}
			<input bind:this="{input}" on:input="{event => onInput(event)}" on:change="{event => onInputChange(event)}" type="text" placeholder="{config.placeholder}" value="{config.value}" class:zes-active="{config.active}"/>
		{:else if ['number', 'more'].includes(config.type)}
			<input bind:this="{input}" on:input="{event => onInput(event)}" on:change="{event => onInputChange(event)}" type="text" inputmode="numeric" pattern="[0-9]*" placeholder="{config.placeholder}" value="{config.value}" class:zes-active="{config.active}"/>
		{:else if config.type === 'select'}
			<select bind:this="{select}" on:change|stopPropagation="{event => onSelectChange(event)}" class:zes-active="{config.active}">
				<option value="">Selecteer een {config.label.toLowerCase()}...</option>
				{#each config.options as option}
					<option value="{option.value}" selected="{(option.value == config.value) || null}">{option.label || option.value}</option>
				{/each}
			</select>
		{/if}
	</div>
	{#if config.warning}
		<div class="zes-input-warning">
			<i class="fa-light fa-triangle-exclamation"></i>
			{config.warning}
		</div>
	{/if}
	{#if config.notice}
		<div class="zes-input-warning">
			<i class="fa-light fa-circle-info"></i>
			{config.notice}
		</div>
	{/if}
	{#if config.image || config.info || config.description}
		<div class="zes-input-description">
			{#if config.image}
				<img src="{Helper.image(config.image)}" alt="{config.label}"/>
			{/if}
			{#if config.info}
				<p>
					<i class="fa-light fa-circle-info"></i>
					{@html config.info}
				</p>
			{/if}
			{#if config.description}
				<div class="zes-input-description-content">
					{@html description(config.description)}
				</div>
			{/if}
		</div>
	{/if}
</div>